import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

export const PricingSection: React.FC = () => {
  const { t } = useTranslation(['landing']);
  const navigate = useNavigate();

  const handleGetStarted = () => {
    navigate('/auth?signup=true');
  };
  return (
    <Box
      id="pricing"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        px: 6,
        py: { xs: 8, md: 12 },
        gap: 8,
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 2,
          width: '100%',
        }}
      >
        <Typography
          sx={{
            fontFamily: '"New Order", sans-serif',
            fontWeight: 500,
            fontSize: { xs: '1.75rem', md: '2.25rem' },
            color: '#004e64',
            textAlign: 'center',
            lineHeight: 1.2,
          }}
        >
          {t('landing:pricing_new.title')}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: '1.125rem',
            color: '#333b3e',
            textAlign: 'center',
            maxWidth: 576,
            lineHeight: 1.6,
          }}
        >
          {t('landing:pricing_new.subtitle')}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          maxWidth: 1024,
          width: '100%',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: 'calc(50% - 6px)' },
            maxWidth: 496,
            minHeight: 510,
          }}
        >
          <Box
            sx={{
              backgroundColor: '#004e64',
              borderRadius: '24px',
              minHeight: 510,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0px 25px 50px -12px rgba(0,0,0,0.25)',
            }}
          >
            <Box sx={{ p: '40px 40px 24px 40px' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 500,
                      fontSize: '2rem',
                      color: '#8cf3f3',
                      lineHeight: 1.2,
                    }}
                  >
                    {t('landing:pricing_new.plans.producer.name')}
                  </Typography>
                  <Box
                    sx={{
                      backgroundColor: '#8cf3f3',
                      borderRadius: '9999px',
                      px: 1.5,
                      py: 0.5,
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 700,
                        fontSize: '0.7rem',
                        color: '#002020',
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        lineHeight: 1.2,
                      }}
                    >
                      {t('landing:pricing_new.plans.producer.popular')}
                    </Typography>
                  </Box>
                </Box>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#ffffff',
                    lineHeight: 1.5,
                  }}
                >
                  {t('landing:pricing_new.plans.producer.description')}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: 5, pb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, whiteSpace: 'nowrap' }}>
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 500,
                    fontSize: '3rem',
                    color: '#8cf3f3',
                    lineHeight: 1,
                  }}
                >
                  {t('landing:pricing_new.plans.producer.price')}
                </Typography>
                <Typography
                  sx={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: 400,
                    fontSize: '1rem',
                    color: '#ffffff',
                    lineHeight: 1.5,
                  }}
                >
                  /
                  <Box component="span" sx={{ fontFamily: '"New Order", sans-serif', fontWeight: 400 }}>
                    {t('landing:pricing_new.period')}
                  </Box>
                </Typography>
              </Box>
            </Box>

            <Box sx={{ px: 5, pb: 3, flex: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                {(t('landing:pricing_new.plans.producer.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                    <CheckIcon sx={{ color: '#8cf3f3', fontSize: 20 }} />
                    <Typography
                      sx={{
                        fontFamily: '"New Order", sans-serif',
                        fontWeight: 400,
                        fontSize: '1rem',
                        color: '#ffffff',
                        lineHeight: 1.5,
                      }}
                    >
                      {feature}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            <Box sx={{ p: '24px 40px 40px 40px' }}>
              <Button
                fullWidth
                onClick={handleGetStarted}
                sx={{
                  backgroundColor: '#8cf3f3',
                  border: '1px solid #8cf3f3',
                  borderRadius: '12px',
                  py: 2.125,
                  px: 0,
                  '&:hover': {
                    backgroundColor: '#7ae0e0',
                    borderColor: '#7ae0e0',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"New Order", sans-serif',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: '#004e64',
                    lineHeight: 1.5,
                  }}
                >
                  {t('landing:pricing_new.cta')}
                </Typography>
              </Button>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            backgroundColor: '#ffffff',
            border: '1px solid #e1e8e7',
            borderRadius: '24px',
            width: { xs: '100%', md: 'calc(50% - 6px)' },
            maxWidth: 496,
            minHeight: 510,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: '40px 40px 24px 40px' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 500,
                  fontSize: '2rem',
                  color: '#003d4d',
                  lineHeight: 1.2,
                }}
              >
                {t('landing:pricing_new.plans.professional.name')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  color: '#41484b',
                  lineHeight: 1.5,
                }}
              >
                {t('landing:pricing_new.plans.professional.description')}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 5, pb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, whiteSpace: 'nowrap' }}>
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 500,
                  fontSize: '3rem',
                  color: '#181c1c',
                  lineHeight: 1,
                }}
              >
                {t('landing:pricing_new.plans.professional.price')}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontWeight: 400,
                  fontSize: '1rem',
                  color: '#41484b',
                  lineHeight: 1.5,
                }}
              >
                /
                <Box component="span" sx={{ fontFamily: '"New Order", sans-serif', fontWeight: 400 }}>
                  {t('landing:pricing_new.period')}
                </Box>
              </Typography>
            </Box>
          </Box>

          <Box sx={{ px: 5, pb: 3, flex: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {(t('landing:pricing_new.plans.professional.features', { returnObjects: true }) as string[]).map((feature: string, index: number) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <CheckIcon sx={{ color: '#004e64', fontSize: 20 }} />
                  <Typography
                    sx={{
                      fontFamily: '"New Order", sans-serif',
                      fontWeight: 400,
                      fontSize: '1rem',
                      color: '#181c1c',
                      lineHeight: 1.5,
                    }}
                  >
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>

          <Box sx={{ p: '24px 40px 40px 40px' }}>
            <Button
              fullWidth
              onClick={handleGetStarted}
              sx={{
                backgroundColor: '#ffffff',
                border: '1px solid #003d4d',
                borderRadius: '12px',
                py: 2.125,
                px: 0,
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#003d4d',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: '"New Order", sans-serif',
                  fontWeight: 500,
                  fontSize: '1rem',
                  color: '#003d4d',
                  lineHeight: 1.5,
                }}
              >
                {t('landing:pricing_new.cta')}
              </Typography>
            </Button>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};